import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import RankHeader from './components/headers/RankHeader';
import useApiControl from './common/useApiControl';

const DEFAULT_LIMIT = 20;

const formatSignedPoint = (value) => {
  if (value === null || value === undefined || Number.isNaN(Number(value))) {
    return '-';
  }
  const numericValue = Number(value);
  const sign = numericValue > 0 ? '+' : '';
  return `${sign}${numericValue.toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  })}`;
};

const formatRate = (value) => {
  if (value === null || value === undefined) {
    return '-';
  }
  return `${(Number(value) * 100).toFixed(1)}%`;
};

function Ranking() {
  const [selectedOption, setSelectedOption] = useState('total');
  const [offset, setOffset] = useState(0);
  const [rankings, setRankings] = useState([]);
  const [pagination, setPagination] = useState({ total: 0, limit: DEFAULT_LIMIT });
  const [options, setOptions] = useState([{ value: 'total', label: '総合収支' }]);
  const [user, setUser] = useState(null);

  const {
    data: userData,
    executeRequest: fetchUser,
  } = useApiControl({
    action: '/get_user',
    method: 'POST',
  });

  const {
    data: masterTagsData,
    executeRequest: fetchMasterTags,
  } = useApiControl({
    action: '/get_master_tags',
    method: 'POST',
  });

  useEffect(() => {
    fetchMasterTags().catch((error) => {
      console.error(error);
    });
  }, [fetchMasterTags]);

  useEffect(() => {
    fetchUser().catch((error) => {
      console.error(error);
    });
  }, [fetchUser]);

  useEffect(() => {
    if (userData) {
      setUser(userData);
    }
  }, [userData]);

  useEffect(() => {
    if (masterTagsData?.tags) {
      setOptions([
        { value: 'total', label: '総合収支' },
        ...masterTagsData.tags.map((tag) => ({
          value: String(tag.id),
          label: tag.name,
        })),
      ]);
    }
  }, [masterTagsData]);

  const rankingParams = useMemo(() => ({
    categoryId: selectedOption === 'total' ? null : Number(selectedOption),
    offset,
    limit: DEFAULT_LIMIT,
  }), [selectedOption, offset]);

  const {
    data: rankingData,
    loading: rankingLoading,
    error: rankingError,
    executeRequest: fetchRankings,
  } = useApiControl({
    action: '/get_rankings',
    params: rankingParams,
    method: 'POST',
  });

  useEffect(() => {
    fetchRankings().catch((error) => {
      console.error(error);
    });
  }, [fetchRankings]);

  useEffect(() => {
    if (rankingData?.rankings) {
      setRankings(rankingData.rankings);
      setPagination(rankingData.pagination ?? { total: 0, limit: DEFAULT_LIMIT });
    }
  }, [rankingData]);

  const handleOptionChange = (event) => {
    setSelectedOption(event.target.value);
    setOffset(0);
  };

  const handlePrev = () => {
    setOffset((prev) => Math.max(prev - DEFAULT_LIMIT, 0));
  };

  const handleNext = () => {
    setOffset((prev) => prev + DEFAULT_LIMIT);
  };

  const hasPrev = offset > 0;
  const hasNext = offset + DEFAULT_LIMIT < (pagination?.total || 0);

  const pointsLabel = selectedOption === 'total' ? '総合収支' : 'カテゴリ別収支ポイント';
  const showingFrom = pagination.total === 0 ? 0 : offset + 1;
  const showingTo = Math.min(offset + DEFAULT_LIMIT, pagination.total);

  return (
    <>
      {user && <RankHeader user={user} />}
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          ランキング一覧
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
          カテゴリ別の収支ポイントと全体の総合収支ランキングを切り替えて確認できます。
        </Typography>
        <FormControl fullWidth sx={{ mb: 3 }}>
          <InputLabel id="ranking-option-label">表示するランキング</InputLabel>
          <Select
            labelId="ranking-option-label"
            value={selectedOption}
            label="表示するランキング"
            onChange={handleOptionChange}
          >
            {options.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {rankingError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            ランキング情報の取得に失敗しました。
          </Alert>
        )}

        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>順位</TableCell>
                <TableCell>名前</TableCell>
                <TableCell align="right">{pointsLabel}</TableCell>
                <TableCell align="right">的中率</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rankingLoading && (
                <TableRow>
                  <TableCell colSpan={4} align="center">
                    <CircularProgress size={24} />
                  </TableCell>
                </TableRow>
              )}
              {!rankingLoading && rankings.length === 0 && (
                <TableRow>
                  <TableCell colSpan={4} align="center">
                    データがありません。
                  </TableCell>
                </TableRow>
              )}
              {!rankingLoading &&
                rankings.map((row) => (
                  <TableRow key={row.user_id}>
                    <TableCell>{row.rank}</TableCell>
                    <TableCell>{row.name}</TableCell>
                    <TableCell align="right">
                      {formatSignedPoint(row.point)}
                    </TableCell>
                    <TableCell align="right">{formatRate(row.win_rate)}</TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </TableContainer>

        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 3 }}>
          <Button variant="outlined" onClick={handlePrev} disabled={!hasPrev}>
            前へ
          </Button>
          <Typography variant="body2" color="text.secondary">
            {pagination.total === 0
              ? '0件'
              : `${showingFrom} - ${showingTo} 件 / 全 ${pagination.total} 件`}
          </Typography>
          <Button variant="contained" onClick={handleNext} disabled={!hasNext}>
            次へ
          </Button>
        </Box>
      </Container>
    </>
  );
}

export default Ranking;
